import { useCallback, useState } from 'react';
import { deleteComplianceDocument } from '@/lib/api';
import { ComplianceDocument } from '@/lib/types';
import { useDownload } from './useDownload';

const getAllFiles = (
  filesData?: ComplianceDocument[],
  filesFlatData?: ComplianceDocument[],
) => [
  ...(filesData ?? []),
  ...(filesFlatData ?? []),
];

export const useComplianceDocumentBulkActions = ({
  filesData,
  filesFlatData,
  onAfterMutation,
}: {
  filesData?: ComplianceDocument[];
  filesFlatData?: ComplianceDocument[];
  onAfterMutation?: () => void;
}) => {
  const [selected, setSelected] = useState<number[]>([]);
  const [removeLoading, setRemoveLoading] = useState(false);
  const [removeModalOpen, setRemoveModalOpen] = useState(false);
  const { download, loading: downloadLoading } = useDownload();

  const handleDownloadClick = useCallback(() => {
    if (!selected.length) return;

    const downloadParams: {
      category: 'compliance_documents';
      file_ids: number[];
      extension?: string;
      entity_name?: string;
    } = {
      category: 'compliance_documents',
      file_ids: selected,
    };

    if (selected.length === 1) {
      const selectedFile = getAllFiles(filesData, filesFlatData).find(
        (file) => file.id === selected[0],
      );
      if (selectedFile?.extension) {
        downloadParams.extension = selectedFile.extension;
      }
      if (selectedFile?.name) {
        downloadParams.entity_name = selectedFile.name;
      }
    }

    download(downloadParams);
  }, [download, filesData, filesFlatData, selected]);

  const handleRemoveClick = useCallback(() => {
    if (!selected.length) return;
    setRemoveModalOpen(true);
  }, [selected]);

  const handleRemoveCancel = useCallback(() => {
    setRemoveModalOpen(false);
  }, []);

  const handleRemoveConfirm = useCallback(async () => {
    setRemoveLoading(true);
    try {
      await Promise.all(
        selected.map((id) => deleteComplianceDocument(id)),
      );
      setSelected([]);
      setRemoveModalOpen(false);
      onAfterMutation?.();
    } finally {
      setRemoveLoading(false);
    }
  }, [onAfterMutation, selected]);

  return {
    selected,
    setSelected,
    handleDownloadClick,
    handleRemoveClick,
    handleRemoveCancel,
    handleRemoveConfirm,
    removeModalOpen,
    downloadLoading,
    removeLoading,
  };
};
